import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { store } from '@/app/store'
import { ResolvedIcon } from '@/features/adp/components/shared/ResolvedIcon'
import { useIntegrationData } from '@/features/adp/hooks/useIntegrationData'
import { syncCanvasState } from '@/features/adp/state/canvas/canvasSlice'
import { sourceService } from '@/features/adp/services/source/source.service'
import { Transformation } from '@/features/adp/services/common/common.types'
import {
  RegisteredSourceFilesResponse,
  SourceFileRegisteredField,
} from '@/features/adp/services/source/source.types'
import { selectCanvasEdges, selectCanvasNodes } from '@/features/adp/state/canvas/canvasSelectors'
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks'
import { JoinIntegrationSheetOverlayData } from '@/features/adp/state/overlaySlice'
import { Button } from '@/ui/button'
import { Label } from '@/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import { Separator } from '@/ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/ui/sheet'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { getAllUpstreamSourceNodes } from '../../canvas/canvas.utils'

const JOIN_TYPES = ['inner', 'left', 'right', 'outer'] as const
type JoinType = (typeof JOIN_TYPES)[number]

/** Persisted per-row Join configuration. One row per secondary join table. */
export interface JoinRowFormData {
  joinType: JoinType
  primaryTableName: string
  primaryJoinKey: string
  /** Comma-joined column names of the primary side at the time this row was saved. */
  primaryColumns: string
  joinTableName: string
  /** `data_source_name` for the secondary source (Angular: `JoinTable_DataSource`). */
  joinTableDataSource: string
  joinTableJoinKey: string
  /** Comma-joined secondary column names (excluding the secondary join key). */
  joinTableColumns: string
  /** Comma-joined data types for `joinTableColumns`, parallel order. Used by
   *  downstream column replay so type-aware dropdowns don't degrade. */
  joinTableColumnTypes?: string
}

interface UpstreamTableSnapshot {
  nodeId: string
  tableName: string
  dataSourceName: string
  columns: Array<{ fieldName: string; dataType: string }>
}

/**
 * Either a fully-resolved upstream source (columns already in node session) or
 * one that needs fetching via `/source-files/registered` because the user
 * hasn't gone through the source's Explorer configuration yet.
 */
interface RawUpstreamEntry {
  nodeId: string
  /** Already-resolved snapshot when present; otherwise the entry needs an API fetch. */
  resolved: UpstreamTableSnapshot | null
  /** Set when `resolved` is null and the source is a registered datasource we can fetch. */
  sourceDataSourceId: number | null
  /** Display name fallback for the source until columns arrive. */
  fallbackDataSourceName: string
}

/**
 * Walk canvas upstream from the Join node and bucket each upstream source as
 * "resolved" (columns already in session) or "needs-fetch" (registered source
 * without an Explorer config yet — we'll pull columns from the API).
 */
function collectRawUpstreamEntries(
  nodeId: string,
  canvasNodes: ReturnType<typeof useAppSelector>,
  canvasEdges: ReturnType<typeof useAppSelector>,
): RawUpstreamEntry[] {
  const sources = getAllUpstreamSourceNodes(
    nodeId,
    canvasNodes as never,
    canvasEdges as never,
  )

  return sources.map((entry): RawUpstreamEntry => {
    const sourceData = (
      entry.sessionData as
        | { sourceData?: { data?: Record<string, unknown> } }
        | null
        | undefined
    )?.sourceData?.data ?? null

    const tableName =
      typeof sourceData?.source_selected_table === 'string'
        ? sourceData.source_selected_table
        : ''
    const dataSourceName =
      typeof sourceData?.source_name === 'string' ? sourceData.source_name : ''
    const files = Array.isArray(sourceData?.source_files)
      ? (sourceData.source_files as SourceFileRegisteredField[])
      : []
    const columns = files
      .map((field) => ({
        fieldName: typeof field.field_name === 'string' ? field.field_name : '',
        dataType:
          typeof field.field_data_type === 'string'
            ? field.field_data_type.toLowerCase()
            : '',
      }))
      .filter((c) => c.fieldName.length > 0)

    const sourceDataSourceId =
      typeof sourceData?.source_data_source_id === 'number'
        ? sourceData.source_data_source_id
        : null

    if (tableName && columns.length > 0) {
      return {
        nodeId: entry.nodeId,
        sourceDataSourceId,
        fallbackDataSourceName: dataSourceName,
        resolved: { nodeId: entry.nodeId, tableName, dataSourceName, columns },
      }
    }

    return {
      nodeId: entry.nodeId,
      sourceDataSourceId,
      fallbackDataSourceName: dataSourceName,
      resolved: null,
    }
  })
}

/**
 * Map the first registered table from `/source-files/registered` into the same
 * snapshot shape the rest of the sheet uses. Returns null if the source has no
 * registered tables yet (user truly hasn't configured anything).
 */
function snapshotFromRegisteredTables(
  nodeId: string,
  dataSourceName: string,
  registered: RegisteredSourceFilesResponse,
): UpstreamTableSnapshot | null {
  const first = registered[0]
  if (!first) return null
  const columns = first.fields
    .map((field) => ({
      fieldName: typeof field.field_name === 'string' ? field.field_name : '',
      dataType:
        typeof field.data_type === 'string' ? field.data_type.toLowerCase() : '',
    }))
    .filter((c) => c.fieldName.length > 0)
  return {
    nodeId,
    tableName: first.table_name,
    dataSourceName,
    columns,
  }
}

/** First column name that appears in both `a` and `b` — used to default join keys. */
function firstCommonColumn(
  a: UpstreamTableSnapshot,
  b: UpstreamTableSnapshot,
): string {
  const aNames = new Set(a.columns.map((c) => c.fieldName))
  for (const col of b.columns) {
    if (aNames.has(col.fieldName)) return col.fieldName
  }
  return ''
}

interface JoinRowDraft {
  joinType: JoinType
  primaryJoinKey: string
  joinTableJoinKey: string
}

const JoinIntegrationSheet = () => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const { isOpen, type, data } = useAppSelector((state) => state.overlay)
  const isVisible = isOpen && type === 'join-integration-sheet'
  const overlayData = isVisible ? (data as JoinIntegrationSheetOverlayData | null) : null

  const canvasNodes = useAppSelector(selectCanvasNodes)
  const canvasEdges = useAppSelector(selectCanvasEdges)
  const { setIntegrationData, getIntegrationDataByNodeId, clearIntegrationData } =
    useIntegrationData()

  const catalog: Transformation[] | undefined = queryClient.getQueryData(['sidebar-data'])?.[2]
  const integrationMeta = useMemo(
    () =>
      catalog?.find((t) => t?.id === Number(overlayData?.transformationId)) ?? null,
    [catalog, overlayData?.transformationId],
  )

  const rawUpstream = useMemo<RawUpstreamEntry[]>(() => {
    if (!overlayData?.nodeId) return []
    return collectRawUpstreamEntries(overlayData.nodeId, canvasNodes, canvasEdges)
  }, [overlayData?.nodeId, canvasNodes, canvasEdges])

  // Fetch `/source-files/registered` for every connected source that doesn't
  // yet have columns in node session. Lets the user pick join keys without
  // having to first open each source's Explorer configuration.
  const fetchQueries = useQueries({
    queries: rawUpstream.map((entry) => {
      const id = entry.sourceDataSourceId ?? 0
      const enabled =
        isVisible && entry.resolved === null && id > 0
      return {
        queryKey: ['registered-source-files', id],
        queryFn: () => sourceService.listRegisteredSourceFiles(id),
        enabled,
        staleTime: Infinity,
      }
    }),
  })

  /**
   * Per-render fingerprint of resolved + fetched-table state. `dataUpdatedAt`
   * only changes when a query actually fetches new data, so this stays stable
   * across no-op re-renders — important because the row-init `useEffect` below
   * depends on `secondaries` and would otherwise reset user edits every render.
   */
  const upstreamFingerprint = rawUpstream
    .map((entry, index) =>
      entry.resolved
        ? `r:${entry.nodeId}:${entry.resolved.tableName}:${entry.resolved.columns.length}`
        : `f:${entry.nodeId}:${fetchQueries[index]?.dataUpdatedAt ?? 0}`,
    )
    .join('|')

  const upstreamTables = useMemo<UpstreamTableSnapshot[]>(() => {
    return rawUpstream
      .map((entry, index): UpstreamTableSnapshot | null => {
        if (entry.resolved) return entry.resolved
        const data = fetchQueries[index]?.data
        if (!data) return null
        return snapshotFromRegisteredTables(
          entry.nodeId,
          entry.fallbackDataSourceName,
          data,
        )
      })
      .filter((s): s is UpstreamTableSnapshot => s !== null)
  }, [rawUpstream, upstreamFingerprint])

  const primary = upstreamTables[0] ?? null
  const secondaries = useMemo(() => upstreamTables.slice(1), [upstreamTables])

  const [rows, setRows] = useState<JoinRowDraft[]>([])

  // Rehydrate from session storage when sheet opens or upstream count changes.
  useEffect(() => {
    if (!isVisible || !overlayData?.nodeId) return

    const stored = getIntegrationDataByNodeId(overlayData.nodeId)
    const storedItems =
      stored?.data &&
      Array.isArray((stored.data as { data?: unknown }).data)
        ? ((stored.data as { data: Array<{ formData?: JoinRowFormData }> }).data)
        : []

    const draftsFromStorage = storedItems.map((item) => ({
      joinType: (item.formData?.joinType ?? 'inner') as JoinType,
      primaryJoinKey: item.formData?.primaryJoinKey ?? '',
      joinTableJoinKey: item.formData?.joinTableJoinKey ?? '',
    }))

    // Align stored rows with current secondaries; trim or pad as needed.
    setRows(
      secondaries.map((sec, index) => {
        const stored = draftsFromStorage[index]
        if (stored) return stored

        // Fresh row — pre-fill keys with first common column between primary and this secondary.
        const commonKey = primary ? firstCommonColumn(primary, sec) : ''
        return {
          joinType: 'inner' as JoinType,
          primaryJoinKey: commonKey,
          joinTableJoinKey: commonKey,
        }
      }),
    )
  }, [isVisible, overlayData?.nodeId, secondaries, primary, getIntegrationDataByNodeId])

  const updateRow = (index: number, patch: Partial<JoinRowDraft>) => {
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    )
  }

  const handleClose = () => dispatch(closeOverlayAsync())

  const handleOpenChange = (open: boolean) => {
    if (!open) handleClose()
  }

  const handleReset = () => {
    if (!overlayData?.nodeId) return
    clearIntegrationData(overlayData.nodeId)
    setRows(
      secondaries.map((sec) => {
        const commonKey = primary ? firstCommonColumn(primary, sec) : ''
        return {
          joinType: 'inner' as JoinType,
          primaryJoinKey: commonKey,
          joinTableJoinKey: commonKey,
        }
      }),
    )
    toast.success('Join configuration reset')
  }

  const handleSave = () => {
    if (!overlayData?.nodeId || !primary || secondaries.length === 0) return

    // Validate every row has both keys set.
    for (let i = 0; i < rows.length; i++) {
      if (!rows[i].primaryJoinKey.trim()) {
        toast.error(`Primary join key required for "${secondaries[i].tableName}"`)
        return
      }
      if (!rows[i].joinTableJoinKey.trim()) {
        toast.error(`Secondary join key required for "${secondaries[i].tableName}"`)
        return
      }
    }

    // Build items + validate dup columns between primary and each secondary.
    const items: Array<{ id: string; label: string; formData: JoinRowFormData }> = []
    /** Column names available on the primary side for the current row (grows as we chain). */
    let runningPrimaryColumns = primary.columns.map((c) => c.fieldName)
    let runningPrimaryTableName = primary.tableName

    for (let i = 0; i < rows.length; i++) {
      const sec = secondaries[i]
      const row = rows[i]
      const secColumnsExcludingKey: string[] = []
      const secColumnTypesExcludingKey: string[] = []
      for (const c of sec.columns) {
        if (c.fieldName === row.joinTableJoinKey) continue
        secColumnsExcludingKey.push(c.fieldName)
        secColumnTypesExcludingKey.push(c.dataType ?? '')
      }

      // Dup detection: any secondary column would collide with the running primary.
      const primarySet = new Set(runningPrimaryColumns)
      const duplicate = secColumnsExcludingKey.find((name) => primarySet.has(name))
      if (duplicate) {
        toast.error(
          `Duplicate column "${duplicate}" detected between primary and "${sec.tableName}". Resolve before saving.`,
        )
        return
      }

      items.push({
        id: crypto.randomUUID(),
        label: `Join ${i + 1}`,
        formData: {
          joinType: row.joinType,
          primaryTableName: runningPrimaryTableName,
          primaryJoinKey: row.primaryJoinKey,
          primaryColumns: runningPrimaryColumns.join(','),
          joinTableName: sec.tableName,
          joinTableDataSource: sec.dataSourceName,
          joinTableJoinKey: row.joinTableJoinKey,
          joinTableColumns: secColumnsExcludingKey.join(','),
          joinTableColumnTypes: secColumnTypesExcludingKey.join(','),
        },
      })

      // Chain: subsequent rows operate on the merged "output" virtual table.
      runningPrimaryTableName = 'output'
      runningPrimaryColumns = [...runningPrimaryColumns, ...secColumnsExcludingKey]
    }

    if (!integrationMeta) {
      toast.error('Catalog entry for Join is missing. Reopen the sheet and try again.')
      return
    }

    setIntegrationData(overlayData.nodeId, {
      id: integrationMeta.id,
      name: integrationMeta.name,
      description: integrationMeta.description,
      abbreviation: integrationMeta.abbreviation,
      data: items,
    })

    // Surface a quick "what's joined" hint on the canvas card. Source and
    // destination nodes set `nodeSecondaryTitle` the same way (e.g.
    // DestinationConfiguration writes the destination name on save).
    const secondaryTitle = secondaries.map(s => s.tableName).filter(Boolean).join(', ')
    if (secondaryTitle) {
      const snapshot = store.getState()
      const nextNodes = snapshot.canvas.present.nodes.map(node => {
        if (node.id !== overlayData.nodeId || node.type !== 'integration') return node
        return {
          ...node,
          data: {
            ...node.data,
            nodeSecondaryTitle: secondaryTitle,
          },
        } as typeof node
      })
      dispatch(
        syncCanvasState({
          nodes: nextNodes,
          edges: snapshot.canvas.present.edges,
        }),
      )
    }

    toast.success('Join configuration saved')
    dispatch(closeOverlayAsync())
  }

  return (
    <Sheet open={isVisible} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        noBackdrop
        topOffset={64}
        className="shadow-none w-full sm:max-w-[640px] flex flex-col"
      >
        <SheetHeader>
          <SheetTitle>Join Details</SheetTitle>
        </SheetHeader>

        <p className="text-sm px-5 text-muted-foreground pb-2">
          Configure how upstream sources are joined together
        </p>
        <Separator />

        <div className="flex items-center gap-3 px-5 py-3">
          <div className="rounded-md border shadow-sm p-2">
            <ResolvedIcon
              imageLink={integrationMeta?.image_link}
              alt={integrationMeta?.name}
              className="h-12 w-12 object-contain"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-sm">{integrationMeta?.name ?? 'Join'}</span>
            <span className="text-xs text-muted-foreground">
              {integrationMeta?.description ?? 'Join multiple upstream sources'}
            </span>
          </div>
        </div>

        <Separator />

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {!primary && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              Connect a Source upstream to start configuring the join.
            </div>
          )}

          {primary && secondaries.length === 0 && fetchQueries.some((q) => q.isPending) && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              Loading source columns…
            </div>
          )}

          {primary &&
            secondaries.length === 0 &&
            !fetchQueries.some((q) => q.isPending) && (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                Connect a second Source upstream to enable join configuration.
              </div>
            )}

          {primary && secondaries.length > 0 && (
            <>
              {/* Primary table — read-only */}
              <div className="rounded-md border p-4 bg-muted/30">
                <Label className="text-xs uppercase text-muted-foreground tracking-wide">
                  Primary Table
                </Label>
                <p className="mt-1 text-sm font-medium">{primary.tableName}</p>
                <p className="text-xs text-muted-foreground">{primary.dataSourceName}</p>
              </div>

              {/* Per-secondary row */}
              {secondaries.map((sec, index) => {
                const row = rows[index]
                if (!row) return null
                /** Columns available on the primary side at row N (grows with chain). */
                const primaryColsForRow = index === 0
                  ? primary.columns.map((c) => c.fieldName)
                  : (() => {
                    const acc = [...primary.columns.map((c) => c.fieldName)]
                    for (let i = 0; i < index; i++) {
                      const prevSec = secondaries[i]
                      const prevKey = rows[i]?.joinTableJoinKey ?? ''
                      acc.push(
                        ...prevSec.columns
                          .map((c) => c.fieldName)
                          .filter((name) => name !== prevKey && !acc.includes(name)),
                      )
                    }
                    return acc
                  })()

                return (
                  <div
                    key={sec.nodeId}
                    className="rounded-md border p-4 space-y-3"
                  >
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground tracking-wide">
                        Join {index + 1} — Secondary Table
                      </Label>
                      <p className="mt-1 text-sm font-medium">{sec.tableName}</p>
                      <p className="text-xs text-muted-foreground">
                        {sec.dataSourceName}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Join Type</Label>
                        <Select
                          value={row.joinType}
                          onValueChange={(value) =>
                            updateRow(index, { joinType: value as JoinType })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {JOIN_TYPES.map((joinType) => (
                              <SelectItem key={joinType} value={joinType}>
                                {joinType.charAt(0).toUpperCase() + joinType.slice(1)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">
                          {index === 0 ? 'Primary Join Key' : 'Output Join Key'}
                        </Label>
                        <Select
                          value={row.primaryJoinKey}
                          onValueChange={(value) =>
                            updateRow(index, { primaryJoinKey: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Key" />
                          </SelectTrigger>
                          <SelectContent>
                            {primaryColsForRow.map((name) => (
                              <SelectItem key={name} value={name}>
                                {name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Secondary Join Key</Label>
                        <Select
                          value={row.joinTableJoinKey}
                          onValueChange={(value) =>
                            updateRow(index, { joinTableJoinKey: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Key" />
                          </SelectTrigger>
                          <SelectContent>
                            {sec.columns.map((col) => (
                              <SelectItem key={col.fieldName} value={col.fieldName}>
                                {col.fieldName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-background">
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!primary || secondaries.length === 0}
          >
            Save Configuration
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default JoinIntegrationSheet
